import Papa from 'papaparse';

export interface SunfireRow {
  confirmationNumber: string;
  submitDate: string;
  applicantPhone: string;
  applicantFirstName: string;
  applicantLastName: string;
  agentName: string;
  agentId: string;
  agentEmail: string;
  // Parsed fields
  date: string;   // YYYY-MM-DD
  time: string;   // HHmmss
}

// Fields we extract — everything else (SSN, HICN, DOB, address) is stripped
const FIELD_MAP: Record<string, keyof SunfireRow> = {
  ConfirmationNumber: 'confirmationNumber',
  ApplicantPhone: 'applicantPhone',
  ApplicantFirstName: 'applicantFirstName',
  ApplicantLastName: 'applicantLastName',
  AgentName: 'agentName',
  AgentID: 'agentId',
  AgentEmail: 'agentEmail',
  SubmitDate: 'submitDate',
};

export function parseSubmitDate(dateStr: string): { date: string; time: string } {
  // Parse "3/23/2026 14:48" → { date: '2026-03-23', time: '144800' }
  if (!dateStr) return { date: '', time: '000000' };
  const parts = dateStr.split(' ');
  const dateParts = parts[0].split('/');
  if (dateParts.length !== 3) return { date: '', time: '000000' };

  const month = dateParts[0].padStart(2, '0');
  const day = dateParts[1].padStart(2, '0');
  const year = dateParts[2];
  const date = `${year}-${month}-${day}`;

  let time = '000000';
  if (parts[1]) {
    const timeParts = parts[1].split(':');
    time = timeParts[0].padStart(2, '0') + (timeParts[1] || '00').padStart(2, '0') + '00';
  }

  return { date, time };
}

export function normalizePhone(phone: string): string {
  if (!phone) return '';
  return phone.replace(/\D/g, '');
}

// Extended row that preserves all CSV fields for ConverselyAI export
export interface SunfireRowFull extends SunfireRow {
  rawFields: Record<string, string>;
}

// PHI fields to strip from rawFields — never leave the browser
const PHI_FIELDS = new Set([
  'ApplicantBirthDate', 'ApplicantSSN', 'ApplicantHICN',
  'ApplicantAddress1', 'ApplicantAddress2', 'ApplicantAddress3',
  'ApplicantCity', 'ApplicantCounty',
  'MailingAddress1', 'MailingAddress2', 'MailingAddress3',
  'MailingCity', 'MailingState', 'MailingZip',
  'ApplicantEmailAddress', 'MedicarePartA', 'MedicarePartB',
  'EmergencyContact', 'EmergencyPhone',
  'OtherCoverageID', 'AuthorizedRepName',
  'AuthorizedRepAddress', 'AuthorizedRepCity',
  'AuthorizedRepState', 'AuthorizedRepZip', 'AuthorizedRepPhone',
  'IPAddress', 'MBI',
]);

// Full parser that preserves all fields (for SMS/ConverselyAI export)
export function parseSunfireCsvFull(fileContent: string): SunfireRowFull[] {
  const result = Papa.parse(fileContent, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (h: string) => h.trim(),
  });

  return result.data.map((raw: any) => {
    const row: Partial<SunfireRowFull> = {};

    // Extract typed fields (same as parseSunfireCsv)
    for (const [csvField, ourField] of Object.entries(FIELD_MAP)) {
      (row as any)[ourField] = (raw[csvField] || '').trim();
    }
    const { date, time } = parseSubmitDate(row.submitDate || '');
    row.date = date;
    row.time = time;
    row.applicantPhone = normalizePhone(row.applicantPhone || '');

    // Preserve all non-PHI fields as rawFields
    const rawFields: Record<string, string> = {};
    for (const [key, value] of Object.entries(raw)) {
      const trimmedKey = key.trim();
      if (!PHI_FIELDS.has(trimmedKey) && typeof value === 'string') {
        rawFields[trimmedKey] = value.trim();
      }
    }
    row.rawFields = rawFields;

    return row as SunfireRowFull;
  }).filter(row => row.confirmationNumber);
}

export function parseSunfireCsv(fileContent: string): SunfireRow[] {
  const result = Papa.parse(fileContent, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (h: string) => h.trim(),
  });

  return result.data.map((raw: any) => {
    const row: Partial<SunfireRow> = {};

    // Extract only safe fields
    for (const [csvField, ourField] of Object.entries(FIELD_MAP)) {
      row[ourField] = (raw[csvField] || '').trim();
    }

    // Parse date + time from SubmitDate
    const { date, time } = parseSubmitDate(row.submitDate || '');
    row.date = date;
    row.time = time;

    // Normalize phone
    row.applicantPhone = normalizePhone(row.applicantPhone || '');

    return row as SunfireRow;
  }).filter(row => row.confirmationNumber); // Skip rows without confirmation number
}
